import { useState, useEffect, useRef } from 'react';
import { Camera, Plus, Trash2, Pencil, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  getMatchPhotos,
  uploadMatchPhoto,
  updateMatchPhoto,
  deleteMatchPhoto,
  getTeams,
  getMatches,
} from '../../lib/api';
import { toast } from 'sonner';

const UploadDialog = ({ open, teams, matches, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState({ title: '', description: '', match_id: '', team_ids: [], taken_at: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setMeta({ title: '', description: '', match_id: '', team_ids: [], taken_at: '' });
    }
  }, [open]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Seleziona un\'immagine');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const toggleTeam = (id) => {
    setMeta((m) => ({
      ...m,
      team_ids: m.team_ids.includes(id) ? m.team_ids.filter(t => t !== id) : [...m.team_ids, id],
    }));
  };

  const handleSave = async () => {
    if (!file) {
      toast.error('Seleziona una foto');
      return;
    }
    setSaving(true);
    try {
      await onUpload(file, {
        title: meta.title.trim() || null,
        description: meta.description.trim() || null,
        match_id: meta.match_id || null,
        team_ids: meta.team_ids,
        taken_at: meta.taken_at || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-photo-dialog">
        <DialogHeader>
          <DialogTitle>Carica una foto</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Dropzone */}
          <label
            htmlFor="photo-input"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            className="block border-2 border-dashed border-slate-700 hover:border-primary rounded-xl p-6 text-center cursor-pointer transition-colors"
          >
            {preview ? (
              <img src={preview} alt="preview" className="max-h-48 mx-auto rounded-lg object-contain" />
            ) : (
              <div className="text-slate-400">
                <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                <p className="text-sm">Trascina un'immagine o clicca per selezionarla</p>
                <p className="text-xs text-slate-500 mt-1">Max 3MB</p>
              </div>
            )}
            <input
              id="photo-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="photo-file-input"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Titolo (opz)</label>
              <Input
                value={meta.title}
                onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="photo-title-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Data (opz)</label>
              <Input
                type="date"
                value={meta.taken_at}
                onChange={(e) => setMeta({ ...meta, taken_at: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Descrizione (opz)</label>
            <Input
              value={meta.description}
              onChange={(e) => setMeta({ ...meta, description: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Partita (opz)</label>
            <Select value={meta.match_id || 'none'} onValueChange={(v) => setMeta({ ...meta, match_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700">
                <SelectValue placeholder="Nessuna" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuna partita</SelectItem>
                {matches.map(m => {
                  const home = teams.find(t => t.id === m.home_team_id)?.name || '?';
                  const away = teams.find(t => t.id === m.away_team_id)?.name || '?';
                  const date = m.match_date ? m.match_date.substring(0, 10) : '';
                  return (
                    <SelectItem key={m.id} value={m.id}>{`${date} · ${home} vs ${away}`}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tagga squadre (opz)</label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {teams.map(t => {
                const active = meta.team_ids.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTeam(t.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      active
                        ? 'bg-primary text-slate-950 border-primary'
                        : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-primary/50'
                    }`}
                    data-testid={`tag-team-${t.id}`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !file}
            className="bg-primary text-slate-950 hover:bg-primary/90"
            data-testid="save-photo-btn"
          >
            {saving ? 'Caricamento...' : 'Carica'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditDialog = ({ open, photo, teams, matches, onClose, onSave }) => {
  const [meta, setMeta] = useState({ title: '', description: '', match_id: '', team_ids: [], taken_at: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && photo) {
      setMeta({
        title: photo.title || '',
        description: photo.description || '',
        match_id: photo.match_id || '',
        team_ids: photo.team_ids || [],
        taken_at: photo.taken_at || '',
      });
    }
  }, [open, photo]);

  const toggleTeam = (id) => {
    setMeta((m) => ({
      ...m,
      team_ids: m.team_ids.includes(id) ? m.team_ids.filter(t => t !== id) : [...m.team_ids, id],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        title: meta.title.trim() || null,
        description: meta.description.trim() || null,
        match_id: meta.match_id || null,
        team_ids: meta.team_ids,
        taken_at: meta.taken_at || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!photo) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-photo-dialog">
        <DialogHeader>
          <DialogTitle>Modifica foto</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <img
            src={`data:${photo.photo_content_type};base64,${photo.photo_data}`}
            alt="preview"
            className="max-h-40 mx-auto rounded-lg object-contain"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Titolo</label>
              <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="bg-slate-800 border-slate-700" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Data</label>
              <Input type="date" value={meta.taken_at} onChange={(e) => setMeta({ ...meta, taken_at: e.target.value })} className="bg-slate-800 border-slate-700" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Descrizione</label>
            <Input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className="bg-slate-800 border-slate-700" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Partita</label>
            <Select value={meta.match_id || 'none'} onValueChange={(v) => setMeta({ ...meta, match_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Nessuna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuna partita</SelectItem>
                {matches.map(m => {
                  const home = teams.find(t => t.id === m.home_team_id)?.name || '?';
                  const away = teams.find(t => t.id === m.away_team_id)?.name || '?';
                  const date = m.match_date ? m.match_date.substring(0, 10) : '';
                  return <SelectItem key={m.id} value={m.id}>{`${date} · ${home} vs ${away}`}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tagga squadre</label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {teams.map(t => {
                const active = meta.team_ids.includes(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggleTeam(t.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      active ? 'bg-primary text-slate-950 border-primary' : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-primary/50'
                    }`}>{t.name}</button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-slate-950 hover:bg-primary/90" data-testid="save-edit-photo-btn">
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminGalleriaPage = () => {
  const [photos, setPhotos] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [globalDrag, setGlobalDrag] = useState(false);
  const inputRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [p, t, m] = await Promise.all([getMatchPhotos(500), getTeams(), getMatches()]);
      setPhotos(p.data || []);
      setTeams(t.data || []);
      setMatches(m.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleUpload = async (file, meta) => {
    try {
      await uploadMatchPhoto(file, meta);
      toast.success('Foto caricata');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
      throw e;
    }
  };

  const handleQuickUpload = async (file) => {
    try {
      await uploadMatchPhoto(file, {});
      toast.success('Foto caricata');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
  };

  const handleSaveEdit = async (data) => {
    try {
      await updateMatchPhoto(editing.id, data);
      toast.success('Foto aggiornata');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
      throw e;
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm('Eliminare questa foto?')) return;
    try {
      await deleteMatchPhoto(p.id);
      toast.success('Foto eliminata');
      fetchAll();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const handleGlobalDrop = async (e) => {
    e.preventDefault();
    setGlobalDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.type?.startsWith('image/')) {
      await handleQuickUpload(file);
    }
  };

  return (
    <div
      className="p-4 sm:p-6 lg:p-8 min-h-screen"
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setGlobalDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setGlobalDrag(false); }}
      onDrop={handleGlobalDrop}
      data-testid="admin-galleria-page"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
              <Camera className="w-7 h-7 text-primary" />
              Galleria foto partite
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {photos.length} foto · Trascina immagini per caricare velocemente
            </p>
          </div>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                for (const f of files) {
                   
                  await handleQuickUpload(f);
                }
                e.target.value = '';
              }}
            />
            <Button
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="border-slate-700 hover:border-primary"
              data-testid="quick-upload-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Carica veloce
            </Button>
            <Button
              onClick={() => setUploadOpen(true)}
              className="bg-primary text-slate-950 hover:bg-primary/90"
              data-testid="upload-photo-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Aggiungi con dettagli
            </Button>
          </div>
        </div>

        {globalDrag && (
          <div className="mb-4 p-6 border-2 border-dashed border-primary rounded-xl bg-primary/5 text-center" data-testid="global-drop-hint">
            <ImageIcon className="w-10 h-10 text-primary mx-auto mb-2" />
            <p className="font-semibold text-primary">Rilascia le immagini per caricarle</p>
          </div>
        )}

        {loading ? (
          <p className="text-slate-400">Caricamento...</p>
        ) : photos.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-700 rounded-xl text-slate-400">
            <Camera className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p>Nessuna foto. Trascina qui un'immagine o usa "Aggiungi".</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p) => {
              const teamNames = (p.team_ids || []).map(id => teams.find(t => t.id === id)?.name).filter(Boolean);
              return (
                <div key={p.id} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-800 bg-slate-900" data-testid={`admin-photo-${p.id}`}>
                  <img
                    src={`data:${p.photo_content_type};base64,${p.photo_data}`}
                    alt={p.title || 'Foto'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-2">
                    {p.title ? <p className="text-xs font-semibold truncate">{p.title}</p> : null}
                    {teamNames.length ? <p className="text-[10px] text-primary truncate">{teamNames.join(' · ')}</p> : null}
                  </div>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditing(p)}
                      className="p-1.5 rounded-lg bg-slate-900/90 border border-slate-700 hover:bg-slate-800"
                      title="Modifica"
                      data-testid={`edit-photo-${p.id}`}
                    >
                      <Pencil className="w-3 h-3 text-white" />
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="p-1.5 rounded-lg bg-slate-900/90 border border-red-500/30 hover:bg-red-500/20"
                      title="Elimina"
                      data-testid={`delete-photo-${p.id}`}
                    >
                      <X className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <UploadDialog
        open={uploadOpen}
        teams={teams}
        matches={matches}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
      />
      <EditDialog
        open={!!editing}
        photo={editing}
        teams={teams}
        matches={matches}
        onClose={() => setEditing(null)}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default AdminGalleriaPage;
